import {
  Button,
  Modal,
  ModalProps,
  Portal,
  Typography,
  useAlert,
  useModal,
} from "matsuri-ui";
import { type Logger } from "@datadog/browser-logs";
import {
  MatsuriFormsQuestion,
  MatsuriFormsQuestionProvider,
  MatsuriFormsTextQuestionProps,
  useMatsuriForms,
} from "matsuri-forms-sdk";
import { PropsWithChildren, createContext, useContext, useState } from "react";
import FeedbackIconBase from "@mui/icons-material/Feedback";
import { MultipleTextQuestion } from "./MultipleTextQuestion";
import { SelectQuestion } from "./SelectQuestion";
import { getRandomId } from "./getRandomId";

/**
 * これはワークアラウンドです。
 * これを行わずに、MuiIconをそのまま使うと、トランスパイル後のコードで次のようなエラーが発生し、コンポーネントを描画できなくなります。
 * `Error: Element type is invalid: expected a string (for built-in components) or a class/function (for composite components) but got: object.`
 * 関連リンク：
 * https://stackoverflow.com/questions/72008357/mui-icons-used-in-shared-react-component-library-wont-render-error-element-ty
 * https://github.com/mui/material-ui/issues/35535
 */
const normalizeIcon = <T,>(Icon: T): T => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  return ((Icon as any).default ? (Icon as any).default : Icon) as T;
};
const FeedbackIcon = normalizeIcon(FeedbackIconBase);

const getFormId = () => {
  return globalThis.M2M_COMPONENTS_ENV === "production"
    ? "01GWRQ1MRS7PE29XGRETFZ6D7Y"
    : "01GTBSYMGPMX91E96P007SXEMH";
};

interface TextQuestionContextValue {
  userId?: string;
  userName?: string;
  serviceName?: string;
  url?: string;
  id?: string;
}

const TextQuestionContext = createContext<TextQuestionContextValue>({});

const TextQuestion = (props: MatsuriFormsTextQuestionProps) => {
  const context = useContext(TextQuestionContext);
  return (
    <input
      value={context[props.title as keyof TextQuestionContextValue]}
      type="hidden"
      name={props.name}
    />
  );
};

interface FeedbackProviderModalProps
  extends Partial<ModalProps>, FeedbackProviderProps {}

const FeedbackProviderModal = (props: FeedbackProviderModalProps) => {
  const { questions, submit } = useMatsuriForms(getFormId(), {
    dev: globalThis.M2M_COMPONENTS_ENV !== "production",
  });

  const { throwAlert } = useAlert();

  const [status, setStatus] = useState<"started" | "sending" | "completed">(
    "started",
  );

  const id = getRandomId();

  return (
    <Modal
      backdrop
      width={600}
      maxWidth={600}
      header={<Typography variant="h3">フィードバックを送信</Typography>}
      body={
        status === "completed" ? (
          <div
            style={{
              display: "grid",
              gap: 32,
            }}
          >
            <Typography>
              レポートを送信しました。ありがとうございます。
            </Typography>
            <Typography color="textSecondary" variant="caption" as="p">
              お送りいただいたフィードバックは、問題の解決とサービスの改善に役立てさせていただきます。
            </Typography>
            <Button
              color="primary"
              variant="filled"
              onClick={() => {
                props.onClose?.();
              }}
            >
              閉じる
            </Button>
          </div>
        ) : (
          <form
            style={{
              display: "grid",
              gap: 24,
            }}
            onSubmit={async (event) => {
              event.preventDefault();
              setStatus("sending");
              const { error } = await submit(event.currentTarget);

              /**
               * 以下、対応が行われるまでは取れる値だけ送る
               * https://github.com/matsuri-tech/matsuri-forms-manager-front/issues/265
               */
              const input = {
                id,
                userId: props.userId,
                userName: props.userName,
                serviceName: props.serviceName,
                url: window.location.href,
              };
              throwAlert(error, {
                ignoreSuccess: true,
                errorMessage: {
                  reason: "サーバー側のエラーの可能性があります。",
                  action:
                    "しばらく時間を空けて試すか、開発チームへお問い合わせください。",
                  happend: "フィードバックの送信に失敗しました。",
                },
                onSuccess: () => {
                  props.datadogLogs?.logger?.info?.(
                    `Feedback: ${id} by ${props.userId}`,
                    {
                      input,
                    },
                  );

                  window.setTimeout(() => {
                    setStatus("completed");
                  }, 2000);
                },
                onError: () => {
                  props.datadogLogs?.logger?.error?.(
                    `Failed Feedback: ${input.id} by ${input.userId}`,
                    { input, error },
                  );

                  window.setTimeout(() => {
                    setStatus("started");
                  }, 2000);
                },
              });
            }}
          >
            <TextQuestionContext.Provider
              value={{
                userId: props.userId,
                userName: props.userName,
                serviceName: props.serviceName,
                url: window.location.href,
                id,
              }}
            >
              <MatsuriFormsQuestionProvider
                renderSelect={SelectQuestion}
                renderMultilineText={MultipleTextQuestion}
                renderText={TextQuestion}
              >
                {questions.map((question) => {
                  return (
                    <MatsuriFormsQuestion {...question} key={question.id} />
                  );
                })}
              </MatsuriFormsQuestionProvider>
            </TextQuestionContext.Provider>
            <div>
              <Button
                type="submit"
                color="primary"
                variant="filled"
                disabled={status === "sending"}
                fullWidth
              >
                送信
              </Button>
              <Typography
                variant="caption"
                style={{
                  float: "right",
                }}
              >
                {status === "sending" ? "送信中..." : undefined}
                &nbsp;
              </Typography>
            </div>
          </form>
        )
      }
      {...props}
    />
  );
};

interface FeedbackProviderProps {
  userId?: string;
  userName?: string;
  serviceName: string;
  datadogLogs?: {
    logger: Logger;
  };
}

export const FeedbackProvider = ({
  children,
  ...modalProps
}: PropsWithChildren<FeedbackProviderProps>) => {
  const [open] = useModal(FeedbackProviderModal, {
    enableClickOutside: true,
    disableClose: false,
  });
  return (
    <div>
      {children}
      <Portal>
        <Button
          style={{
            position: "fixed",
            bottom: 16,
            right: 16,
          }}
          onClick={() => {
            open(modalProps);
          }}
          type="button"
          icon={<FeedbackIcon />}
          color="secondary"
          variant="filled"
          rounded
        />
      </Portal>
    </div>
  );
};
